import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PLANS, TENANT_STATUSES, type PlanCode, type TenantStatus } from '@naya/shared'
import { AdminLayout } from '../components/AdminLayout.js'
import { StatusPill } from '../components/StatusPill.js'
import { useTenantList } from '../hooks/useTenants.js'
import type { TenantRow } from '../types.js'

const PLAN_LABEL: Record<PlanCode, string> = {
  starter: 'Starter',
  pro: 'Pro',
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function TableSkeleton() {
  return (
    <tbody>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: 5 }).map((_, j) => (
            <td key={j}>
              <div className="admin-skel" style={{ width: `${50 + ((i + j) % 4) * 12}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  )
}

export function TenantList() {
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useTenantList()

  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all')
  const [planFilter, setPlanFilter] = useState<PlanCode | 'all'>('all')
  const [search, setSearch] = useState('')

  const filtered = useMemo<TenantRow[]>(() => {
    const rows = data?.tenants ?? []
    const term = search.trim().toLowerCase()
    return rows.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false
      if (planFilter !== 'all' && t.plan_tier !== planFilter) return false
      if (term && !t.name.toLowerCase().includes(term)) return false
      return true
    })
  }, [data, statusFilter, planFilter, search])

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <h1>Tenants</h1>
        <button
          type="button"
          className="admin-btn admin-btn-primary"
          onClick={() => navigate('/admin/tenants/new')}
        >
          Create tenant
        </button>
      </div>

      <div className="admin-filters">
        <select
          className="admin-select"
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as TenantStatus | 'all')
          }
        >
          <option value="all">All statuses</option>
          {TENANT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </select>
        <select
          className="admin-select"
          aria-label="Filter by plan tier"
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value as PlanCode | 'all')}
        >
          <option value="all">All plans</option>
          {PLANS.map((p) => (
            <option key={p} value={p}>
              {PLAN_LABEL[p]}
            </option>
          ))}
        </select>
        <input
          className="admin-input"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search tenants by name"
        />
      </div>

      {isError ? (
        <div className="admin-error-state">
          <p>Couldn't load tenants.</p>
          <button
            type="button"
            className="admin-btn"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="admin-card" style={{ padding: 0 }}>
          <div className="admin-table-wrap admin-table-wrap--has-cards">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <TableSkeleton />
            </table>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="admin-card">
          <div className="admin-empty">
            {(data?.tenants.length ?? 0) === 0
              ? 'No tenants yet. Create your first one.'
              : 'No tenants match these filters.'}
          </div>
        </div>
      ) : (
        <>
          {/* Desktop: table. Hidden < 640px via CSS. */}
          <div className="admin-card" style={{ padding: 0 }}>
            <div className="admin-table-wrap admin-table-wrap--has-cards">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => navigate(`/admin/tenants/${t.id}`)}
                    >
                      <td>{t.name}</td>
                      <td className="admin-audit-meta">{t.slug}</td>
                      <td>{PLAN_LABEL[t.plan_tier]}</td>
                      <td>
                        <StatusPill status={t.status} />
                      </td>
                      <td className="admin-audit-meta">
                        {formatDate(t.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile: stacked cards. Shown < 640px via CSS. */}
          <div className="admin-tenant-cards">
            {filtered.map((t) => (
              <div
                key={t.id}
                className="admin-tenant-card"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/tenants/${t.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(`/admin/tenants/${t.id}`)
                  }
                }}
              >
                <div className="admin-tenant-card-top">
                  <span className="admin-tenant-card-name">{t.name}</span>
                  <StatusPill status={t.status} />
                </div>
                <div className="admin-tenant-card-meta">
                  <span>{t.slug}</span>
                  <span>·</span>
                  <span>{PLAN_LABEL[t.plan_tier]}</span>
                  <span>·</span>
                  <span>{formatDate(t.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  )
}
